import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Text,
    Paper,
    Alert,
    Table,
    Checkbox,
    Group,
} from '@mantine/core';
import { IconDatabase } from '@tabler/icons-react';
import EntitlementsService from '../../services/EntitlementsService';
import type { EntityEntitlementForSchemaResponse } from '../../types/EntityEntitlements';
import { getTier2AttributesFromResponse, isEntitySetFacade } from '../../types/EntityEntitlements';
import { LoadingState } from './LoadingState';

export interface Tier2SchemaTabViewProps {
    /** Entity type key (used when fetching or for labels when data is provided by parent). */
    entityType: string;
    /** When provided, use this data instead of fetching (e.g. from EntitlementViewer). Avoids loading EntitlementsService in that path. */
    tier2Data?: EntityEntitlementForSchemaResponse | null;
    /** When tier2Data is provided, use this for loading state. */
    tier2Loading?: boolean;
}

/** Top-level tier2 keys we show in the primary table (exclude AttributeBindings/Attributes; they have their own table). */
const TOP_LEVEL_KEYS_ORDER = ['Entity', 'CreateEntity', 'DeleteEntity', 'EditEntity', 'IsAdministrator', 'ReadableAttributeIds', 'Requestor', 'PolicyRules'];

function getCellDisplay(value: unknown): string {
    if (value === undefined || value === null) return '—';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        return value.map((item) => {
            if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
                const obj = item as Record<string, unknown>;
                if (typeof obj.DisplayName === 'string') return obj.DisplayName;
                if (typeof obj.Id === 'string') return obj.Id;
                return JSON.stringify(item);
            }
            return String(item);
        }).join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

const PREFERRED_ATTR_COLUMN_ORDER = [
    'Id',
    'DisplayName',
    'Type',
    'Description',
    'Array',
    'Indexed',
    'Searchable',
    'Encrypted',
    'IsReference',
    'IsReferenceTo',
    'HasLengthCount',
    'Count',
    'Length',
    'Required',
    'Mutability',
    'MultiValued',
    'CanonicalValues'
];

function getAttributeCellValue(value: unknown): string {
    if (value === undefined || value === null) return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

/**
 * Table-level Schema tab: shows a table of attributes from tier2 entitlement only.
 * Full tier2 response is available under Raw > Ent.
 * Displays all keys present in the attribute objects (fixed order for known columns, then rest alphabetically).
 */
export const Tier2SchemaTabView: React.FC<Tier2SchemaTabViewProps> = ({
    entityType,
    tier2Data: tier2DataFromParent,
    tier2Loading: tier2LoadingFromParent
}) => {
    const [tier2Fetched, setTier2Fetched] = useState<EntityEntitlementForSchemaResponse | null | undefined>(undefined);
    const [loadingFetched, setLoadingFetched] = useState(true);
    const [wordWrap, setWordWrap] = useState(false);

    const parentSuppliesData = tier2DataFromParent !== undefined;
    const tier2 = parentSuppliesData ? tier2DataFromParent : tier2Fetched;
    const loading = parentSuppliesData ? (tier2LoadingFromParent ?? false) : loadingFetched;

    useEffect(() => {
        if (parentSuppliesData || !entityType?.trim()) {
            if (!parentSuppliesData) {
                setTier2Fetched(undefined);
                setLoadingFetched(false);
            }
            return;
        }
        const key = entityType.trim().toLowerCase();
        const cached = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(key);
        if (cached != null) {
            setTier2Fetched(cached);
            setLoadingFetched(false);
            return;
        }
        setLoadingFetched(true);
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(key)
            .then((data) => setTier2Fetched(data ?? null))
            .catch(() => setTier2Fetched(null))
            .finally(() => setLoadingFetched(false));
    }, [entityType, parentSuppliesData]);

    const isFacade = isEntitySetFacade(tier2 ?? undefined);
    const attributeBindings = isFacade ? undefined : tier2?.AttributeBindings;
    const attributes = useMemo(() => getTier2AttributesFromResponse(tier2 ?? undefined), [tier2]);
    const attributesForTable = attributes.length > 0 ? attributes : (tier2 === null ? undefined : null);
    const topLevelRows = useMemo(() => {
        if (!tier2 || typeof tier2 !== 'object') return [];
        const keys = new Set(Object.keys(tier2));
        keys.delete('Attributes');
        keys.delete('AttributeBindings');
        const ordered = TOP_LEVEL_KEYS_ORDER.filter((k) => keys.has(k));
        const rest = [...keys].filter((k) => !TOP_LEVEL_KEYS_ORDER.includes(k)).sort();
        return [...ordered, ...rest].map((key) => ({ key, value: (tier2 as Record<string, unknown>)[key] }));
    }, [tier2]);

    const columns = useMemo(() => {
        if (!Array.isArray(attributesForTable) || attributesForTable.length === 0) return [];
        const keySet = new Set<string>();
        for (const attr of attributesForTable) {
            if (attr && typeof attr === 'object') {
                for (const k of Object.keys(attr)) keySet.add(k);
            }
        }
        const ordered = PREFERRED_ATTR_COLUMN_ORDER.filter((k) => keySet.has(k));
        const rest = [...keySet].filter((k) => !PREFERRED_ATTR_COLUMN_ORDER.includes(k)).sort();
        return [...ordered, ...rest];
    }, [attributesForTable]);

    /** Rows for the third table: only AttributeBindings entries that have Required, RegEx, or AllowedValues. */
    const bindingRows = useMemo(() => {
        if (!Array.isArray(attributeBindings) || attributeBindings.length === 0) return [];
        return attributeBindings
            .map((ab) => {
                const b = ab.Binding;
                const hasRequired = b?.Required === true;
                const hasRegEx = b?.RegEx != null && String(b.RegEx).trim() !== '';
                const hasAllowed = Array.isArray(b?.AllowedValues) && b.AllowedValues.length > 0;
                if (!hasRequired && !hasRegEx && !hasAllowed) return null;
                const attr = ab.Attribute;
                return {
                    Id: attr?.Id ?? b?.Attribute ?? '—',
                    DisplayName: attr?.DisplayName ?? '—',
                    Type: attr?.Type ?? '—',
                    Required: hasRequired,
                    RegEx: hasRegEx ? b!.RegEx : '—',
                    AllowedValues: hasAllowed ? (b!.AllowedValues as unknown[]) : []
                };
            })
            .filter((r): r is NonNullable<typeof r> => r != null);
    }, [attributeBindings]);

    if (loading) {
        return (
            <LoadingState variant="centered" minHeight={200} label={`Loading tier2 for ${entityType}...`} />
        );
    }

    if (tier2 === null || tier2 === undefined) {
        return (
            <Box p="sm">
                <Alert color="blue">
                    No tier2 data for this entity type. Open the table or Raw &gt; Ent to load it.
                </Alert>
            </Box>
        );
    }

    return (
        <Box p="sm">
            {/* Top-level tier2 properties (Entity, EditEntity, IsAdministrator, etc.) */}
            {topLevelRows.length > 0 && (
                <>
                    <Paper p="sm" withBorder mb="sm">
                        <Text size="md" fw={600} mb="xs">
                            Top-level (tier2)
                        </Text>
                        <Text size="sm" c="dimmed" mb="xs">
                            From GET .../mgmt/Entitlements/entity/&#123;schemaEntityId&#125; (excluding AttributeBindings).
                        </Text>
                        <Table.ScrollContainer minWidth={500}>
                            <Table
                                withTableBorder
                                withColumnBorders
                                striped
                                highlightOnHover
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th fw={600} w={200}>Property</Table.Th>
                                        <Table.Th fw={600}>Value</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {topLevelRows.map(({ key, value }) => (
                                        <Table.Tr key={key}>
                                            <Table.Td ff="monospace" style={{ verticalAlign: 'top' }}>{key}</Table.Td>
                                            <Table.Td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} title={getCellDisplay(value)}>
                                                {getCellDisplay(value)}
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    </Paper>
                </>
            )}

            {/* Attributes table */}
            <Paper p="sm" withBorder mb="sm">
                <Group gap="xs" mb="xs">
                    <IconDatabase color="var(--mantine-color-blue-6)" />
                    <Text component="span" fw={600} size="lg">
                        Attributes (tier2)
                    </Text>
                    <Checkbox
                        checked={wordWrap}
                        onChange={(e) => setWordWrap(e.currentTarget.checked)}
                        size="sm"
                        label="Word wrap"
                        ml="md"
                    />
                </Group>
                <Text size="sm" c="dimmed">
                    From GET .../mgmt/Entitlements/entity/&#123;schemaEntityId&#125;. Full response under Raw &gt; Ent.
                </Text>
            </Paper>
            {!Array.isArray(attributesForTable) || attributesForTable.length === 0 ? (
                <Alert color="blue" mb="sm">
                    No attributes in tier2 for {entityType}.
                </Alert>
            ) : (
            <Paper withBorder>
                <Box style={{ maxHeight: 360, overflowY: 'auto', overflowX: 'auto' }}>
                    <Table.ScrollContainer minWidth={500}>
                        <Table striped highlightOnHover>
                            <Table.Thead>
                            <Table.Tr>
                                {columns.map((col) => (
                                    <Table.Th
                                        key={col}
                                        fw={600}
                                        bg="var(--mantine-color-body)"
                                        style={{
                                            whiteSpace: 'nowrap',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            boxShadow: '0 1px 0 0 var(--mantine-color-default-border)'
                                        }}
                                    >
                                        {col}
                                    </Table.Th>
                                ))}
                            </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                            {attributesForTable.map((attr, index) => (
                                <Table.Tr key={String((attr as Record<string, unknown>).Id ?? (attr as Record<string, unknown>).DisplayName ?? index)}>
                                    {columns.map((col) => {
                                        const value = (attr as Record<string, unknown>)[col];
                                        const isIdOrType = col === 'Id' || col === 'Type';
                                        const isRefTo = col === 'IsReferenceTo';
                                        const isDescription = col === 'Description';
                                        const wrapCell = wordWrap && (isDescription || isRefTo);
                                        const truncateCell = !wrapCell && (isDescription || isRefTo);
                                        return (
                                            <Table.Td
                                                key={col}
                                                ff={isIdOrType ? 'monospace' : undefined}
                                                style={{
                                                    maxWidth: isDescription ? 280 : isRefTo ? 220 : undefined,
                                                    overflow: truncateCell ? 'hidden' : undefined,
                                                    textOverflow: truncateCell ? 'ellipsis' : undefined,
                                                    whiteSpace: wrapCell ? 'normal' : 'nowrap'
                                                }}
                                                title={!wordWrap && (isDescription || isRefTo) && value != null ? getAttributeCellValue(value) : undefined}
                                            >
                                                {getAttributeCellValue(value)}
                                            </Table.Td>
                                        );
                                    })}
                                </Table.Tr>
                            ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Box>
            </Paper>
            )}

            {/* Third table: only attributes that have binding constraints (Required, RegEx, or AllowedValues). Not available for entity-set facades (e.g. Person). */}
            {isFacade && (
                <Alert color="blue" mt="sm">
                    No bindings available for this entity type — it is an entity-set facade (e.g. Person as &quot;All Users&quot;), not a real schema entity. AttributeBindings are only present for concrete entity types.
                </Alert>
            )}
            {!isFacade && bindingRows.length > 0 && (
                <Paper p="sm" withBorder mt="md">
                    <Text size="md" fw={600} mb="xs">
                        Attribute bindings
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                        Attributes with Required, RegEx, or AllowedValues (from AttributeBindings).
                    </Text>
                    <Table.ScrollContainer minWidth={500}>
                        <Table
                            withTableBorder
                            withColumnBorders
                            striped
                            highlightOnHover
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th fw={600}>Id</Table.Th>
                                    <Table.Th fw={600}>DisplayName</Table.Th>
                                    <Table.Th fw={600}>Type</Table.Th>
                                    <Table.Th fw={600}>Required</Table.Th>
                                    <Table.Th fw={600} miw={120}>RegEx</Table.Th>
                                    <Table.Th fw={600}>AllowedValues</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {bindingRows.map((row, idx) => (
                                    <Table.Tr key={String(row.Id ?? idx)}>
                                        <Table.Td ff="monospace">{getAttributeCellValue(row.Id)}</Table.Td>
                                        <Table.Td>{getAttributeCellValue(row.DisplayName)}</Table.Td>
                                        <Table.Td ff="monospace">{getAttributeCellValue(row.Type)}</Table.Td>
                                        <Table.Td>{row.Required ? 'true' : '—'}</Table.Td>
                                        <Table.Td ff="monospace" maw={200} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={typeof row.RegEx === 'string' ? row.RegEx : undefined}>
                                            {getAttributeCellValue(row.RegEx)}
                                        </Table.Td>
                                        <Table.Td maw={180} title={Array.isArray(row.AllowedValues) ? row.AllowedValues.join(', ') : undefined}>
                                            {Array.isArray(row.AllowedValues) && row.AllowedValues.length > 0 ? row.AllowedValues.join(', ') : '—'}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Paper>
            )}
        </Box>
    );
};
